import { useContext, useEffect, useRef, useState } from 'react';

import useDetectClick from  '@/components/hooks/useDetectClick';
import { MaxCapType, WorkflowType, plannerEvent, plannerEventCount, user } from  '@/interfaces/planner';
import AddEvent from './AddEvent';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import useHTTP from  '@/components/hooks/useHTTP';
import EventDate from './EventDate';
import { BuildPath } from  '@/helpers/Paths';
import { PlannerContext } from '@/context/PlannerContext';
import { ToastMessageType } from '../global/ToastNotification';
import { ToastData } from  '@/interfaces/LockerDetails';
import ScrollingAttendeesList from './ScrollingAttendeesList';
import { timeToDecimal } from  '@/helpers/Date';
import { IconLibrary, ThriveIcon } from 'thrive-components';



interface EventPreviewDialogueProps {
    onClose: () => void,
    evtId: number,
    alignment?: string,
    setToast:(toastData:ToastData) => void,
}

const EventPreviewDialogue = ({onClose, evtId, alignment='left-align', setToast}: EventPreviewDialogueProps) => {

    const [selectedAttendee, setSelectedAtendees] = useState<'Confirmed' | 'Awaiting' | 'No'>('Confirmed')
    const [evt, setEvt] = useState<plannerEvent>();
    const [eventCount, setEventCount] = useState<plannerEventCount>()
    const [attendeeList, setAttendeeList] = useState<user[]>([])
    const [attendeesLoading, setAttendeesLoading] = useState<boolean>(false);
    const [detailsLoaded, setDetailsLoaded] = useState<boolean>(false)
    const [countLoaded, setCountLoaded] = useState<boolean>(false)
    const { getEvents, selectedCal, calendarMessage } = useContext(PlannerContext)
    const [awaitingUsers, setAwaitingUsers] = useState<user[]>([]);
    const [totalAwaitingUsers, setTotalAwaitingUsers] = useState<number>(0)
    const [processingUsers, setProccessingUsers] = useState<number[]>([]);

    const showAddModal = useRef<boolean>(false);
    const showArchiveModal = useRef<boolean>(false);
    const hasEvt = useRef<boolean>(false);

    const { get, put } = useHTTP();

    const awaitingUsersLengthRef = useRef<number>(0);

    useEffect(() => {
        awaitingUsersLengthRef.current = awaitingUsers.length
    }, [awaitingUsers.length])

    useDetectClick(`container-${evtId}`, (clickedInContainer) => {
        
        if(!hasEvt.current) {
            return
        }
        if (!clickedInContainer){ 
            if(!(showAddModal.current  || showArchiveModal.current)) {
                onClose();
            }
        }
    }, true);
    
    useEffect(() => {
        if(calendarMessage.timestamp) {
            const attendingType = {
                Confirmed: 'attending',
                Awaiting: 'awaiting',
                No: 'notattending'
            }[selectedAttendee]

            getEvent();

            get(`planner/calendar/${selectedCal.value}/events/${evtId}/invitees/${attendingType}?skip=0&take=8`)
                .then(({data}) => setAttendeeList(data.items));

            get(`planner/calendar/${selectedCal.value}/events/${evtId}/invitees/count`)
                .then(({data}) => {
                    setEventCount(data)
                });
            
            getAwaitingUsers(0, awaitingUsersLengthRef.current > 8 ? awaitingUsersLengthRef.current : 8, true);
            setProccessingUsers([]);
        }
    }, [calendarMessage.timestamp])
    

    useEffect(() => {
        getEvent();
        
        getEventCount();
        
        getAwaitingUsers();
    }, []);

    useEffect(() => {
        setAttendeesLoading(true)
        
        const attendingType = {
            Confirmed: 'attending',
            Awaiting: 'awaiting',
            No: 'notattending'
        }[selectedAttendee]

        get(`planner/calendar/${selectedCal.value}/events/${evtId}/invitees/${attendingType}?skip=0&take=8`)
            .then(({data}) => setAttendeeList(data.items))
            .finally(() => setAttendeesLoading(false))
    }, [selectedAttendee])

    function hasLoaded(){
        return countLoaded && detailsLoaded
    }

    function getEvent(){
        get(`planner/calendar/${selectedCal.value}/events/${evtId}`)
            .then(({data}) => {
                setEvt({
                    ...data,
                    occurrence: {
                        ...data.occurrence,
                        start: new Date(data.occurrence.start),
                        end: new Date(data.occurrence.end)
                    }

                });
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setDetailsLoaded(true);
                hasEvt.current = true;
            })

    }

    function getAwaitingUsers(skip=0, top=8, refresh=false) {
        get(`planner/calendar/${selectedCal.value}/events/${evtId}/invitees/approvalRequired?skip=${skip}&take=${top}`)
            .then(({data}) => {
                setAwaitingUsers(refresh ? data.items : [...awaitingUsers, ...data.items]);
                setTotalAwaitingUsers(data.totalItemCount)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    function getEventCount(){
        get(`planner/calendar/${selectedCal.value}/events/${evtId}/invitees/count`)
            .then(({data}) => {
                setEventCount(data)
            })
            .finally(() => {
                setCountLoaded(true);
            });
    }
    

    function getTotal(){
        return {
            Confirmed: eventCount?.attendingCount,
            Awaiting: eventCount?.awaitingResponseCount,
            No: eventCount?.declinedCount
        }[selectedAttendee]
    }

    function getPlaceHolder() {
        switch(selectedAttendee) {
            case 'Confirmed':
                return 'No confirmed attendees yet.'
            case 'Awaiting':
                return 'All attendees have responded'
            case 'No':
                return 'No non-attendees yet.'
        }
    }

    function closeAndRefresh(){
        showAddModal.current=false;
        if(selectedCal){
           getEvents(selectedCal.value); 
        }  
    }

    function formatDate(st:Date, et:Date){
        const startDate =  Intl.DateTimeFormat(undefined, {dateStyle: 'short' }).format(st);
        const endDate = Intl.DateTimeFormat(undefined, {dateStyle: 'short' }).format(et);

        if (startDate === endDate) {
            return (startDate)
        }

        return `${startDate} - ${endDate}`
    }

    function isGatedEvent(){
        return evt && evt.workflowType === WorkflowType.SingleApprovalWorkflow && awaitingUsers.length > 0;
    }

    function eventIsFull(){
        if(evt?.maximumCapacityType === MaxCapType.attendees){
            return Number(evt.currentAttendingCount) >= Number(evt.maximumCapacity)
        }
        else if (evt?.maximumCapacityType === MaxCapType.time) {
            return (timeToDecimal(String(evt.currentTimeAllocated)) >= Number(evt.maximumCapacity))
        }
        return false;
        
    }


    return (
        <div className={`event-preview-dialogue-container container-${evtId} ${alignment}`} style={{height: isGatedEvent() ? '300px' : ''}}>
            {hasLoaded() &&
                <>
                    <div className='event-info-container'>
                        <div className='title-wrapper'>
                            <h3>{evt?.title || 'Placeholder' }</h3> 

                            <button
                                name={evt?.title ? `Delete Event: ${evt.title}` : 'Delete Event'}
                                aria-label={evt?.title ? `Delete Event: ${evt.title}` : 'Delete Event'}
                                onClick={() => showArchiveModal.current = true}
                                disabled={evt?.isCancelled}
                            >
                                <ThriveIcon 
                                    iconName={IconLibrary.BIN}
                                    color={evt?.isCancelled ? "var(--thriveGreyDark)" : "var(--thriveRed)"}
                                    sizeInPx='12'
                                />
                            </button>
                        </div>
                        <ul>
                            <li>
                                <ThriveIcon 
                                    iconName={IconLibrary.CALENDAR}
                                    sizeInPx='12'
                                    color={"var(--thriveGreyDark)"}
                                />

                                <span className='info-item'>{formatDate(evt.occurrence.start, evt.occurrence.end)}</span>
                            </li>
                            
                            <li>
                                <ThriveIcon 
                                    iconName={IconLibrary.CLOCK}
                                    sizeInPx='12'
                                    color={"var(--thriveGreyDark)"}
                                />

                                <span className='info-item'>
                                    <EventDate event={evt} />
                                </span>
                            </li>
                            
                            {!!evt?.location?.name && 
                                <li>
                                    <ThriveIcon 
                                        iconName={IconLibrary.MAP_PIN}
                                        sizeInPx='12'
                                        color={"var(--thriveGreyDark)"}
                                    />

                                    <span className='info-item'>{evt?.location?.name}</span>
                                </li>
                            }
                        </ul>
                    </div>

                    {isGatedEvent() && 
                        <ScrollingAttendeesList
                            awaitingUsers={awaitingUsers}
                            getAwaitingUsers={getAwaitingUsers}
                            eventWorkflowId={evt?.eventWorkflowId}
                            processingUsers={processingUsers}
                            setProcessingUsers={setProccessingUsers}
                            totalAwaitingUsers={totalAwaitingUsers}
                            isFull={eventIsFull()}
                            eventTimeRemaining={evt?.maximumCapacityType === 2 ? evt.maximumCapacity - timeToDecimal(evt.currentTimeAllocated) : undefined}
                            isCancelled={!!evt?.isCancelled}
                            isClosed={evt?.registrationStatus == 'Closed'}
                        />
                    }

                    {(!attendeesLoading  || !!eventCount?.totalCount) && 
                        <div className='attendees-container'>
                            <h3>Invited ({eventCount?.totalCount})</h3>

                            <ul>
                                <li><button className={`${selectedAttendee === 'Confirmed' ? 'selected' : ''}`} onClick={() => setSelectedAtendees('Confirmed')}>Confirmed</button></li>
                                <li><button className={`${selectedAttendee === 'Awaiting' ? 'selected' : ''}`} onClick={() => setSelectedAtendees('Awaiting')}>Awaiting</button></li>
                                <li><button className={`${selectedAttendee === 'No' ? 'selected' : ''}`} onClick={() => setSelectedAtendees('No')}>No</button></li>
                            </ul>

                            {attendeeList.length > 0
                                ?   <div className='attendees-profile-container'>
                                        {attendeeList.map((i) => 
                                            <img 
                                                key={`attendee-${i.id}`}
                                                alt={`${i.displayName} profile pic`}
                                                src={i.profilePicUrl ||`${BuildPath()}/images/locker/icons/default-profile.png`}
                                                onError={(e) => (e.target as HTMLImageElement).src=`${BuildPath()}/images/locker/icons/default-profile.png`}
                                            />
                                        )}

                                        {attendeeList.length === 8 && Number(getTotal()) > 8 &&
                                            <div className='more-images'>
                                                <span>+{Number(getTotal()) - 8}</span>
                                            </div>
                                        }
                                    </div>
                                :   <div className='attendees-profile-container no-attendees'>
                                        <p>{getPlaceHolder()}</p>
                                    </div>
                            }
                        </div>
                    }
                    <div className='event-preview-dialogue-container-controls-wrapper'>
                        <button onClick={() => {showAddModal.current=true}}>View Event</button>
                        <div className='report-wrapper'>
                            <span className='response-percent'>
                                <ThriveIcon iconName={IconLibrary.FILE_RESPONSE} sizeInPx='12' color={"var(--thriveCMSTextEmphasisColor)"}/>
                                {`${eventCount?.replyRate}%`}
                            </span>
                            
                            <a className='download-response' target='__blank' href={`../api/planner/calendar/${selectedCal.value}/events/${evtId}/report`}>
                                Report 
                                <ThriveIcon iconName={IconLibrary.DOWNLOAD} sizeInPx='12' color='var(--thriveCMSTextEmphasisColor)'/>
                            </a>
                        </div>
                    </div>
                    
                    <ModalWrapper className='add-event-modal' isOpen={showAddModal.current} escClose={showAddModal.current}  onRequestClose={() => (closeAndRefresh())} hideCloseButton={true}>    
                        <AddEvent plusDate={new Date()}  closeModal={() => (closeAndRefresh())} setToast={setToast} eventCount={eventCount} event={evt} />
                    </ModalWrapper>

                    <ModalWrapper className='archive-event-modal' isOpen={showArchiveModal.current} escClose={false}  onRequestClose={() => {}} hideCloseButton={true}>    
                        <p>Are you sure you want to archive this event? We will notify all attendees and invitees this event is now cancelled.</p>
                        
                        <div className='archive-modal-button-wrapper'>
                            <button onClick={() => showArchiveModal.current = false}>Cancel</button>
                            
                            <button className='archive' onClick={() => {
                                showArchiveModal.current = false
                                put(`planner/calendar/${selectedCal.value}/events/${evt?.id}/cancel`, {eventId: evt?.id})
                                .then(() => {
                                    setToast({type: ToastMessageType.SUCCESS, message: `${evt?.title} has been cancelled.`, show: true})
                                })
                                .catch(() => {
                                    setToast({type: ToastMessageType.WARNING, message: `${evt?.title} has not been cancelled.`, show: true})
                                })
                                .finally(() => {
                                    getEvents(selectedCal.value);
                                })
    
                            }}>Send Cancellation</button>
                        </div>
                    </ModalWrapper>
                </>
            }
        </div>
    )
}

export default EventPreviewDialogue;